import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Modal, Row } from "react-bootstrap";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { IAvailableOption, IBooking, IOptionSegment, getApiCallState, useStateSelector } from "./Model";
import { useDispatch } from "react-redux";
import { loadOptions, updateOptions } from "./Actions";
import { getOptionGroup, optionGroups } from "../TravelDetails/Options";
import { NumberSelector } from "../Components/NumberSelector";
import { isTest } from "../S3BookingApp";
import { Price } from "../Components/Price";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { clearApiCallState } from "../ApiCalls/Actions";
import { formatteraTal } from "../Shared/Utils";
import { getKrogenProductCodes } from "../TravelDetails/KrogenOptionsHelpers";

const commonStrings = getStrings().component.common;
const optionStrings = getStrings().component.options;

const krogenProductCodes = getKrogenProductCodes();

export const EditOptionsModal = (props: { show: boolean, onClosed: (optionsChanged: boolean) => void }) =>
{
	const dispatch = useDispatch();

	const booking = useStateSelector(state => state.manageBooking.booking);
	const apicalls = getApiCallState();
	const loadOptionsState = apicalls.getLoadingState("loadOptions");
	const updateOptionsState = apicalls.getLoadingState("updateOptions");


	const [changedOptions, setChangedOptions] = useState<{segmentId: string, tariffCode: string, count: number, price: number}[]>([]);

	const amount = changedOptions?.reduce((sum, opt) => sum + opt.price*opt.count, 0) || 0;

	useEffect(() => {
		if (!booking?.bookingNumber || !props.show)
			return;

		dispatch(loadOptions(booking.bookingNumber));
	}, [booking?.bookingNumber, props.show]);

	useEffect(() => {
		return () => {
			console.debug("EditOptionsModal DESTROYED");
			setChangedOptions([]);
			dispatch(clearApiCallState("updateOptions"));
		};
	}, [props.show]);

	const optionCountChanged = (segment: IOptionSegment, option: IAvailableOption, newCount: number, currentCount: number) =>
	{
		console.debug("optionCountChanged", segment, option, newCount, currentCount);

		const temp = [...changedOptions];
		const itemIndex = temp.findIndex(x => x.segmentId === segment.id && x.tariffCode === option.tariffCode);

		if (newCount <= currentCount && itemIndex >= 0) //Ta bort om det är samma som tidigare (eller färre, men det ska det inte kunna bli)
			temp.splice(itemIndex, 1);
		else if (newCount > currentCount && itemIndex >= 0) //Uppdatera antal om den redan finns
			temp[itemIndex].count = newCount;
		else if (newCount > currentCount) //Annars lägg till den
			temp.push({ segmentId: segment.id, tariffCode: option.tariffCode, count: newCount, price: option.price });

		setChangedOptions(temp);
	};

	const submit = () => {
		if (booking?.bookingNumber && changedOptions?.length > 0)
			dispatch(updateOptions(booking.bookingNumber, changedOptions));
	};

	if (!booking)
		return null;

	const showDates = booking.availableOptions?.segments.some(y => y.segment.departureDate != booking.availableOptions.segments[0].segment.departureDate);

	const outbound = booking?.outbound ? booking?.availableOptions?.segments.filter(x => booking.outbound.segments.some(y => y.segmentId === x.segment.id)) : null;
	const inbound = booking?.inbound ? booking?.availableOptions?.segments.filter(x => booking.inbound.segments.some(y => y.segmentId === x.segment.id)) : null;


	return <Modal show={props.show} centered backdrop="static" size={outbound && inbound ? "xl" : "lg"}>
		<Modal.Header><Modal.Title>{optionStrings.editOptions.modalTitle}</Modal.Title></Modal.Header>

		{ updateOptionsState === "loaded" ? <>
			<Modal.Body>
				<h4>{optionStrings.editOptions.optionsUpdatedHeading}</h4>
				<p>{optionStrings.editOptions.optionsUpdatedText}</p>
				{booking?.totalPriceToBePaid > 0 && <p>{optionStrings.editOptions.optionsPaymentText.replace("{totalPriceToBePaid}", formatteraTal(booking.totalPriceToBePaid, null, 2, 0))}</p>}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => props.onClosed(true)}>{commonStrings.close}</Button>
			</Modal.Footer>
		</> : <>
			<Modal.Body>
				{ loadOptionsState === "loading" && <Alert variant="light">{optionStrings.editOptions.loadingOptions} <SpinnerSmall /></Alert>}
				{ loadOptionsState === "loaded" && !(booking?.availableOptions?.segments?.length > 0) && <Alert variant="warning">{optionStrings.editOptions.noOptionsAvailable}</Alert>}

				<Row>
					{ outbound && <Col sm={12} xl={inbound ? 6 : 12}>
						{inbound && <h4>{optionStrings.editOptions.headingOutbound}</h4>}
						{outbound.map(x => <SegmentOptions key={x.segment.id} segment={x.segment} options={x.options} showDate={showDates} booking={booking} disabled={updateOptionsState === "loading"}
																onOptionCountChanged={(opt, newCount, currentCount) =>  optionCountChanged(x.segment, opt, newCount, currentCount)} />)}
					</Col> }
					{ inbound && <Col sm={12} xl={outbound ? 6 : 12}>
						{outbound && <h4>{optionStrings.editOptions.headingReturn}</h4>}
						{inbound.map(x => <SegmentOptions key={x.segment.id} segment={x.segment} options={x.options} showDate={showDates} booking={booking} disabled={updateOptionsState === "loading"}
																onOptionCountChanged={(opt, newCount, currentCount) =>  optionCountChanged(x.segment, opt, newCount, currentCount)} />)}
					</Col> }
				</Row>

				{ isTest() && <code>{JSON.stringify(changedOptions)}</code> }

				<div className="text-right mt-3">
					{optionStrings.editOptions.optionSumText}: <strong>{amount} {commonStrings.sek}</strong>
				</div>

				<Alert variant="danger" className="mt-3" show={updateOptionsState === "error"}>{optionStrings.editOptions.submitError}</Alert>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => props.onClosed(false)}>{commonStrings.abort}</Button>
				<Button variant="primary" disabled={changedOptions.length === 0 || updateOptionsState === "loading"} onClick={submit}>
					{optionStrings.editOptions.submitChangesNoFee} {updateOptionsState === "loading" && <SpinnerSmall />}
				</Button>
			</Modal.Footer>
		</> }
	</Modal>;
}

const SegmentOptions = (props: { options: IAvailableOption[], segment: IOptionSegment, showDate: boolean, booking: IBooking, disabled?: boolean,
						onOptionCountChanged: (option: IAvailableOption, newCount: number, currentCount) => void }) => {

	const [showCurrent, setShowCurrent] = useState(false);

	const maxCount = props.booking.passengers.length;

	const groupedOptions = props.options.filter(x => !krogenProductCodes.includes(x.code)).reduce((acc, curr) => {
			const groupName = getOptionGroup(curr.code) || "others";
			const sortOrder = optionGroups[groupName] ? optionGroups[groupName].sortOrder : 100;
			let group = acc.find(x => x.name === groupName);
			if (!group)
			{
				group = { name: groupName, sortOrder, options: [] };
				acc.push(group);
			}

			group.options.push(curr);

			return acc;
	}, [] as {name: string, sortOrder: number, options: IAvailableOption[]}[]).sort((a, b) => a.sortOrder - b.sortOrder);

	const currentOptions = (props.segment.direction === "outward" ? props.booking.outbound : props.booking.inbound)?.segments?.find(x => x.segmentId === props.segment.id)?.options;

	return <div>
		<h5>{props.segment.origin?.name} - {props.segment.destination?.name}{props.showDate && `, ${props.segment.departureDate}`}</h5>
		{ groupedOptions.map(g => <div key={g.name}>
				<h5>{optionStrings.optionGroups[g.name] ?? g.name}</h5>
				<div className="option-selector">
					{g.options.sort((a,b) => a.name > b.name ? 1 : -1).map(opt => {
						const currentCount = currentOptions?.find(x => x.code === opt.code)?.count || 0;
						return <Row key={opt.code} className="mb-1">
							<Col xs={8} sm={9}><label htmlFor={`option_${props.segment.id}_${opt.code}`}>{opt.name} (<Price amount={opt.price} />){isTest() && <Badge variant="light" className="ml-4">{opt.code}</Badge>}</label></Col>
							<Col xs={4} sm={3}>
								<NumberSelector inputId={`option_${props.segment.id}_${opt.code}`} min={currentCount} max={maxCount} initialValue={currentCount}
									onValueChanged={n => props.onOptionCountChanged(opt, n, currentCount)} disabled={props.disabled} />
							</Col>
						</Row>;
					})}
				</div>
			</div>
		)}

		{ isTest() && <>
			{ showCurrent ? <>
				<strong>Befintliga tillval</strong><br />
				<code style={{whiteSpace: "pre"}}>{currentOptions?.length > 0 ? JSON.stringify(currentOptions, null, 2) : "Inga befintliga tillval för detta segment"}</code>
			</> : <Button variant="link" onClick={() => setShowCurrent(true)}>Visa befintliga tillval</Button>}
		</>}

	</div>;
};